'use client';

import { useState } from 'react';
import { useFoundry } from '@/lib/foundry/context';

export function ExportPanel() {
  const { state, actions } = useFoundry();
  const plan = state.currentPlan;
  const [downloadingMd, setDownloadingMd] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);

  const handleDownloadMarkdown = async () => {
    setDownloadingMd(true);
    try {
      await actions.downloadMarkdown();
    } finally {
      setDownloadingMd(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!plan) return;

    setDownloadingPdf(true);
    try {
      // Dynamic import to avoid SSR issues
      const { pdf } = await import('@react-pdf/renderer');
      const { FoundryPDFDocument } = await import('./pdf/FoundryPDFDocument');

      const doc = FoundryPDFDocument({ plan });
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `business-plan-${plan.name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!plan) return;

    setDownloadingDocx(true);
    try {
      // Dynamic import to avoid SSR issues
      const { createFoundryWordDocument } = await import('./word/FoundryWordDocument');

      const blob = await createFoundryWordDocument(plan);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `business-plan-${plan.name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Word generation error:', error);
      alert('Failed to generate Word document. Please try again.');
    } finally {
      setDownloadingDocx(false);
    }
  };

  if (!plan) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent"></div>
      </div>
    );
  }

  const hasContent = plan.lean_canvas?.problem !== null || (plan.sections && plan.sections.length > 0);

  if (!hasContent) {
    return (
      <div className="p-6 bg-yellow-500/20 border border-yellow-500/40 rounded-lg text-center">
        <p className="text-yellow-300">
          Add some content to your business plan before exporting.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-foreground">Export Your Business Plan</h2>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Markdown Export */}
        <div className="p-6 bg-background/40 border border-border-muted rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-2">Markdown</h3>
          <p className="text-foreground-tertiary text-sm mb-4">
            Download your business plan as a Markdown file. Perfect for further editing or importing into other tools.
          </p>
          <button
            onClick={handleDownloadMarkdown}
            disabled={downloadingMd}
            className="w-full px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {downloadingMd ? 'Downloading...' : 'Download Markdown'}
          </button>
        </div>

        {/* PDF Export */}
        <div className="p-6 bg-background/40 border border-border-muted rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-2">PDF</h3>
          <p className="text-foreground-tertiary text-sm mb-4">
            Generate a professional PDF document with your complete business plan.
          </p>
          <button
            onClick={handleDownloadPDF}
            disabled={downloadingPdf}
            className="w-full px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {downloadingPdf ? 'Generating PDF...' : 'Download PDF'}
          </button>
        </div>

        {/* Word Export */}
        <div className="p-6 bg-background/40 border border-border-muted rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-2">Word</h3>
          <p className="text-foreground-tertiary text-sm mb-4">
            Export to Microsoft Word format (.docx) for easy editing and collaboration.
          </p>
          <button
            onClick={handleDownloadWord}
            disabled={downloadingDocx}
            className="w-full px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {downloadingDocx ? 'Generating Word...' : 'Download Word'}
          </button>
        </div>
      </div>

      {/* Plan Summary */}
      <div className="p-6 bg-background/40 border border-border-muted rounded-lg">
        <h3 className="text-lg font-semibold text-foreground mb-4">Plan Summary</h3>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-foreground-muted">Name</dt>
            <dd className="text-foreground font-medium">{plan.name}</dd>
          </div>
          <div>
            <dt className="text-foreground-muted">Status</dt>
            <dd className="text-foreground font-medium">{plan.status.replace('_', ' ')}</dd>
          </div>
          <div>
            <dt className="text-foreground-muted">Industry</dt>
            <dd className="text-foreground font-medium">{plan.industry || 'Not set'}</dd>
          </div>
          <div>
            <dt className="text-foreground-muted">Sections</dt>
            <dd className="text-foreground font-medium">
              {plan.sections?.filter((s) => s.content_md).length || 0} / 4
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
